import { useCallback, useEffect, useState } from "react";

const COLLAPSE_KEY = "scroll.sidebarCollapsed";
const EVENT_NAME = "sidebarCollapsedChange";
const AUTO_COLLAPSE_QUERY = "(min-width: 768px) and (max-width: 1279px)";
// Keep auto-collapse opt-outs page-local so stale browser storage cannot break the default rail.
let expandedOverride = false;

function read(): boolean | null {
  if (typeof window === "undefined") return false;
  const value = window.localStorage.getItem(COLLAPSE_KEY);
  if (value === "1") return true;
  return null;
}

function matchesAutoCollapse(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(AUTO_COLLAPSE_QUERY).matches;
}

export function useSidebarCollapsed() {
  const [preferredCollapsed, setPreferredCollapsed] = useState<boolean | null>(read);
  const [autoCollapsed, setAutoCollapsed] = useState<boolean>(matchesAutoCollapse);
  const [forceExpanded, setForceExpanded] = useState(expandedOverride);
  const collapsed = preferredCollapsed === true || (autoCollapsed && !forceExpanded);

  useEffect(() => {
    const handler = () => {
      setPreferredCollapsed(read());
      setForceExpanded(expandedOverride);
    };
    window.addEventListener(EVENT_NAME, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(AUTO_COLLAPSE_QUERY);
    const handler = () => setAutoCollapsed(media.matches);
    handler();
    media.addEventListener("change", handler);
    return () => media.removeEventListener("change", handler);
  }, []);

  const toggle = useCallback(() => {
    const next = !collapsed;
    if (typeof window !== "undefined") {
      expandedOverride = !next && autoCollapsed;
      if (next) {
        window.localStorage.setItem(COLLAPSE_KEY, "1");
      } else {
        window.localStorage.removeItem(COLLAPSE_KEY);
      }
      window.dispatchEvent(new Event(EVENT_NAME));
    }
    setPreferredCollapsed(next);
    setForceExpanded(expandedOverride);
  }, [autoCollapsed, collapsed]);

  return { collapsed, toggle };
}
